import * as React from "react";
import { Session, WebPortalClient } from "webportalclient";
import { DateMatchSortHeader } from "../Components/DateMatchSortHeader";
import { MatchSortHeader } from "../Components/MatchSortHeader";
import { useQuery } from "../Components/Query";
import { Column, Table } from "../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import { AnalysisAcceptabilityStatus } from "../misc/components/analysis/AnalysisAcceptabilityStatus";
import { Authorization } from "../misc/components/Authorization";
import { Link } from "react-router-dom";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../Core/SecurityContext";
import { formatDateTime } from "../misc/components/Formatters/Formatters";
import { SpirometryAnalysisSetTypeEnum } from "../misc/api/ApiSpirometryAnalysisSetType";
import { DlcoAnalysisSetTypeEnum } from "../misc/api/ApiDlcoAnalysisSetType";
import { PressureAnalysisSetTypeEnum } from "../misc/api/ApiPressureAnalysisSetTypeEnum";
import { LungVolumeAnalysisSetTypeEnum } from "../misc/api/ApiLungVolumeAnalysisSetType";
import { AnalysisSet } from "webportalclient";
import Condition from "../misc/components/Condition";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { uniqBy } from "../misc/ArrayFunctions";
import { CoughAnalysisSetTypeEnum } from "../misc/api/CoughAnalysisSetTypes";
import { EcgAnalysisSetTypeEnum } from "../misc/api/ApiEcgAnalysisSetType";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { OscillometryAnalysisSetTypeEnum } from "../misc/api/ApiOscillometryAnalysisSetTypeEnum";

interface SessionListHamburgerCoreProps {
    row: Session;
}

const SessionListHamburgerCore = ({ row }: SessionListHamburgerCoreProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    const technicianVisibleAnalysisSet = row.analysisSets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Site
        || x.type === DlcoAnalysisSetTypeEnum.Site
        || x.type === PressureAnalysisSetTypeEnum.Site
        || x.type === LungVolumeAnalysisSetTypeEnum.Site
        || x.type === EcgAnalysisSetTypeEnum.Overread
        || x.type === OscillometryAnalysisSetTypeEnum.Site
    );

    if (technicianVisibleAnalysisSet === null || technicianVisibleAnalysisSet === undefined)
        return (
            <>
                <Authorization permissionId={PermissionEnum.AccessIndividualDiaryResults} studyId={study.id} currentPermissions={currentUserPermissions}>
                    <Condition test={() => row.sessionType === SessionTypes.EDiary}>
                        <li>
                            <Link to={`/diary/results/${row.visit.subjectId}`}>View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.AccessAllBloodGlucoseTests} studyId={study.id} currentPermissions={currentUserPermissions}>
                    <Condition test={() => row.sessionType === SessionTypes.BloodGlucose}>
                        <li>
                            <Link to={`/bloodglucose/tests/${row.id}`}>View Tests</Link>
                        </li>
                    </Condition>
                </Authorization>
            </>
        )

    return (
        <>
            <Authorization permissionId={PermissionEnum.AccessSingleFvcSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Fvc}>
                    <li>
                        <Link to={{ pathname: `/spirometry/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.SiteReviewFvcSession} studyId={study.id} currentPermissions={currentUserPermissions}>
                <Condition test={() => row.sessionType === SessionTypes.Fvc}>
                    <li>
                        <Link to={{ pathname: `/spirometry/siteanalysis/${technicianVisibleAnalysisSet.id}/edit`, state: { from: location.pathname } }}>Review</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleDlcoSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Dlco}>
                    <li>
                        <Link to={{ pathname: `/dlco/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleMipSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Mip}>
                    <li>
                        <Link to={{ pathname: `/mip/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleMepSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Mep}>
                    <li>
                        <Link to={{ pathname: `/mep/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleSnipSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Snip}>
                    <li>
                        <Link to={{ pathname: `/snip/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleLungVolumeSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.LungVolumeHelium || row.sessionType === SessionTypes.LungVolumePleth}>
                    <li>
                        <Link to={{ pathname: `/lungvolume/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleSvcSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Svc}>
                    <li>
                        <Link to={{ pathname: `/svc/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleECGSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Ecg}>
                    <li>
                        <Link to={{ pathname: `/ecg/${row.id}`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSingleOscillometrySession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Oscillometry}>
                    <li>
                        <Link to={{ pathname: `/oscillometry/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.AccessSinglePefSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                <Condition test={() => row.sessionType === SessionTypes.Pef}>
                    <li>
                        <Link to={{ pathname: `/pef/analysis/${technicianVisibleAnalysisSet.id}/view`, state: { from: location.pathname } }}>View</Link>
                    </li>
                </Condition>
            </Authorization>
        </>
    );
}

export const SessionsListTable = () => {
    const { data, applyFilters } = useQuery();
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [sessionTypeOptions, setSessionTypeOptions] = React.useState([]);
    const [technicians, setTechnicians] = React.useState([]);

    React.useEffect(() => {
        const getSites = async () => {
            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {
            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await client.client.getStudyScheduleVisits("id,label", `studySchedule/studyId eq ${study.id}`, "label");

            const options = uniqBy(visits.value, (a, b) => a.label === b.label).map(s => ({
                value: s.label,
                label: s.label,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {
            const scheduleSessions = await client.client.getStudyScheduleSessions("id,label", `studyScheduleVisit/studySchedule/studyId eq ${study.id}`, "label");

            const sessions = await client.client.getSessions("sessionType,sessionTypeName,technician", `visit/subject/site/studyId eq ${study.id}`);

            const options = uniqBy(scheduleSessions.value, (a, b) => a.label === b.label).map(s => ({
                value: s.label,
                label: s.label,
            }));

            setSessionOptions(options);

            const sessionTypes = uniqBy(sessions.value, (a, b) => a.sessionType === b.sessionType).map(s => ({
                value: s.sessionType,
                label: s.sessionTypeName,
            }));

            setSessionTypeOptions(sessionTypes);

            const technicians = uniqBy(sessions.value, (a, b) => a.technician === b.technician).map(s => ({
                value: s.technician,
                label: s.technician,
            }));

            setTechnicians(technicians);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
    }, [location.search]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const formatSiteStatus = (sets: AnalysisSet[]) => {
        const overreadSet = sets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Site
            || x.type === DlcoAnalysisSetTypeEnum.Site
            || x.type === PressureAnalysisSetTypeEnum.Site
            || x.type === LungVolumeAnalysisSetTypeEnum.Site
            || x.type === OscillometryAnalysisSetTypeEnum.Site
        );

        return (<>
            <AnalysisAcceptabilityStatus value={overreadSet ? overreadSet?.status : null} /><br />
        </>);
    }

    const formatOverreadingStatus = (sets: AnalysisSet[]) => {
        const overreadSet = sets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Overread
            || x.type === DlcoAnalysisSetTypeEnum.Overread
            || x.type === PressureAnalysisSetTypeEnum.Overread
            || x.type === LungVolumeAnalysisSetTypeEnum.Overread
            || x.type === EcgAnalysisSetTypeEnum.Overread
            || x.type === OscillometryAnalysisSetTypeEnum.Overread
        );

        const coughSet = sets.find(x => x.type === CoughAnalysisSetTypeEnum.Analysis);

        return (<>
            <AnalysisAcceptabilityStatus value={overreadSet ? overreadSet?.status : (coughSet ? coughSet.status : null)} /><br />
        </>);
    }

    const RenderHamburgerMenu = (row: Session) => {
        return (
            <HamburgerMenu>
                <SessionListHamburgerCore row={row} />
            </HamburgerMenu>
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Sessions</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table data={data}>
                                    <Column field="visit" header={<MatchSortHeader title="Site ID" field="visit/subject/site/id" options={siteOptions} sortField="visit/subject/site/number" />} formatter={visit => visit.subject.site.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Subject ID" field="visit/subject/id" options={subjectOptions} sortField="visit/subject/number" />} formatter={visit => visit.subject.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Visit" field="visit/name" options={visitOptions} sortField="visit/name" />} formatter={visit => visit.name} />
                                    <Column field="name" header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" />} formatter={name => name} />
                                    <Column field="sessionTypeName" header={<MatchSortHeader title="Session Type" field="sessionType" options={sessionTypeOptions} sortField="sessionType" />} formatter={sessionTypeName => sessionTypeName} />
                                    <Column field="technician" header={<MatchSortHeader title="Technician" field="technician" options={technicians} sortField="technician" />} formatter={technician => technician} />
                                    <Column field="occurred" header={<DateMatchSortHeader title="Session Start" field="occurred" sortField="occurred" />} formatter={occurred => formatDateTime(occurred)} />
                                    <Column field="lastReceived" header={<DateMatchSortHeader title="Last Received" field="lastReceived" sortField="lastReceived" />} formatter={lastReceived => formatDateTime(lastReceived)} />
                                    <Column field="analysisSets" header="Site Status" formatter={status => formatSiteStatus(status)} />
                                    <Column field="analysisSets" header="Over-read Status" formatter={status => formatOverreadingStatus(status)} />
                                    <Column field="" formatter={RenderHamburgerMenu} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
